import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ChevronLeft, HandHeart, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/**
 * General Freedom In Me volunteer form (spec §6).
 * NOT event-specific — captures interest across every FIM program (Business
 * Showers, 5K, 2027 planning, etc.). Submissions land in
 * `volunteer_submissions` and flow into the admin dashboard.
 */
export default function Volunteer() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    city: "", state: "", organization: "", availability: "",
    preferred_contact: "email", skills: "", reason: "",
    consent_email: true, consent_sms: false,
    interests: [], fim_5k_details: [],
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const { data } = await api.get("/event/volunteer/config");
        if (m) setConfig(data);
      } catch { /* noop */ }
    })();
    return () => { m = false; };
  }, []);

  const toggleInterest = (label) => {
    setForm((f) => {
      const has = f.interests.includes(label);
      const next = has ? f.interests.filter((x) => x !== label) : [...f.interests, label];
      // Clear 5K sub-interests if 5K unchecked
      const clear5k = has && label === "Freedom In Me 5K";
      return { ...f, interests: next, fim_5k_details: clear5k ? [] : f.fim_5k_details };
    });
  };
  const toggle5k = (label) => setForm((f) => ({
    ...f,
    fim_5k_details: f.fim_5k_details.includes(label)
      ? f.fim_5k_details.filter((x) => x !== label)
      : [...f.fim_5k_details, label],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please complete first name, last name, email, and mobile.");
      return;
    }
    if (form.interests.length === 0) {
      toast.error("Please pick at least one area you're interested in.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/event/volunteer/submit", form);
      setDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/40 to-white">
        <div className="mx-auto max-w-lg px-5 py-10">
          <div data-testid="vol-success" className="rounded-3xl bg-emerald-950 text-white p-6 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-fuchsia-500/30 blur-3xl" />
            <div className="relative">
              <CheckCircle2 size={40} className="text-emerald-300 mb-3" />
              <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-300">Thank you</div>
              <h1 className="font-[Cormorant_Garamond] text-3xl leading-tight mt-1">Thank you for your interest in volunteering with Freedom In Me.</h1>
              <p className="mt-3 text-sm font-[Outfit] text-white/80 leading-relaxed">{done.message}</p>
              <div className="mt-6 grid grid-cols-2 gap-2">
                <Link to="/event" data-testid="volunteer-done-home" className="rounded-full h-11 flex items-center justify-center bg-white text-emerald-950 hover:bg-emerald-100 font-[Outfit] font-bold text-sm">Event Hub</Link>
                <Link to="/" data-testid="volunteer-done-fim" className="rounded-full h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 font-[Outfit] font-bold text-sm">Freedom In Me</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-emerald-800/40" />
      </div>
    );
  }

  const has5k = form.interests.includes("Freedom In Me 5K");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/40 to-white text-emerald-950">
      <div className="mx-auto max-w-lg px-5 py-8">
        <Link
          to="/"
          data-testid="volunteer-back"
          className="inline-flex items-center gap-1 text-xs font-[Outfit] font-bold uppercase tracking-widest text-emerald-800/60 hover:text-fuchsia-700 mb-4"
        >
          <ChevronLeft size={14} /> Freedom In Me home
        </Link>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 px-3 py-1 mb-3">
          <HandHeart size={12} className="text-fuchsia-700" />
          <span className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-800">General Volunteer</span>
        </div>
        <h1 className="font-[Cormorant_Garamond] text-4xl leading-[1.02]">Volunteer With <span className="text-fuchsia-500">Freedom In Me</span></h1>
        <p className="mt-2 font-[Outfit] text-sm text-emerald-950/70 leading-relaxed">
          Tell us a little about yourself and how you’d like to help. We’ll match you with opportunities that fit your interests.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="First name *" testid="vol-first" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required />
            <Field label="Last name *" testid="vol-last" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required />
          </div>
          <Field label="Email *" type="email" testid="vol-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          <Field label="Mobile *" type="tel" testid="vol-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
          <div className="grid grid-cols-2 gap-2">
            <Field label="City" testid="vol-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <Field label="State" testid="vol-state" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
          </div>
          <Field label="Business or organization (optional)" testid="vol-org" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} />
          <Field label="Availability (evenings, weekends, remote…)" testid="vol-availability" value={form.availability} onChange={(v) => setForm({ ...form, availability: v })} />
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Preferred communication</span>
            <select
              data-testid="vol-preferred"
              value={form.preferred_contact}
              onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })}
              className="mt-1 h-11 w-full rounded-md border-2 border-emerald-100 bg-white px-3 text-sm font-[Outfit]"
            >
              <option value="email">Email</option>
              <option value="text">Text message</option>
              <option value="phone">Phone call</option>
            </select>
          </label>
          <TextArea label="Relevant experience or skills (optional)" testid="vol-skills" value={form.skills} onChange={(v) => setForm({ ...form, skills: v })} />
          <TextArea label="Why are you interested in volunteering? (optional)" testid="vol-reason" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} />

          <div className="rounded-2xl bg-emerald-50/40 border border-emerald-100 p-4">
            <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-fuchsia-700 mb-2">Interest areas · pick as many as apply *</div>
            <div className="grid grid-cols-1 gap-1.5">
              {config.interest_areas.map((label) => {
                const on = form.interests.includes(label);
                return (
                  <label
                    key={label}
                    data-testid={`vol-interest-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border-2 transition-colors ${on ? "border-fuchsia-500 bg-fuchsia-50" : "border-emerald-100 bg-white hover:border-emerald-300"}`}
                  >
                    <input type="checkbox" checked={on} onChange={() => toggleInterest(label)} className="h-4 w-4 rounded" />
                    <span className="text-sm font-[Outfit] text-emerald-950">{label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {has5k && (
            <div data-testid="vol-5k-details" className="rounded-2xl bg-white border-2 border-fuchsia-200 p-4">
              <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-fuchsia-700 mb-2">Freedom In Me 5K · specific interests</div>
              <div className="grid grid-cols-1 gap-1.5">
                {config.five_k_sub_interests.map((label) => {
                  const on = form.fim_5k_details.includes(label);
                  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  return (
                    <label key={label} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${on ? "border-fuchsia-500 bg-fuchsia-50" : "border-emerald-100 bg-white"}`}>
                      <input
                        data-testid={`vol-5k-sub-${slug}`}
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle5k(label)}
                        className="h-4 w-4 rounded"
                      />
                      <span className="text-sm font-[Outfit] text-emerald-950">{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-2">
            <label className="flex items-start gap-2 text-xs font-[Outfit] text-emerald-900 leading-relaxed">
              <input type="checkbox" checked={form.consent_email} onChange={(e) => setForm({ ...form, consent_email: e.target.checked })} className="h-4 w-4 mt-0.5 rounded" data-testid="vol-consent-email" />
              <span>Email me about volunteer opportunities and updates.</span>
            </label>
            <label className="flex items-start gap-2 text-xs font-[Outfit] text-emerald-900 leading-relaxed">
              <input type="checkbox" checked={form.consent_sms} onChange={(e) => setForm({ ...form, consent_sms: e.target.checked })} className="h-4 w-4 mt-0.5 rounded" data-testid="vol-consent-sms" />
              <span>
                Send me text messages about volunteer opportunities.
                Msg &amp; data rates may apply. Reply STOP to opt out.
                See our <a href="/privacy" className="underline">Privacy</a> &amp; <a href="/terms" className="underline">Terms</a>.
              </span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="vol-submit"
            className="w-full h-12 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-[Outfit] font-bold text-sm shadow-lg shadow-fuchsia-500/30 mt-2"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Submit volunteer interest"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">{label}</span>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1 h-11 rounded-md font-[Outfit]"
      />
    </label>
  );
}
function TextArea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">{label}</span>
      <Textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="mt-1 rounded-md font-[Outfit]"
      />
    </label>
  );
}
